<template>
  <v-app>
    <!-- Hero Header -->
    <div class="hero-header">
      <div class="hero-content">
        <div class="hero-flex">
          <div>
            <h1 class="hero-title">
              <v-icon dark large class="mr-2" style="opacity: 0.85"
                >mdi-account-search</v-icon
              >
              ระบบค้นหาบุคลากร
            </h1>
            <p class="hero-subtitle">
              Personnel Search System — สืบค้นข้อมูลบุคลากรในสังกัด
            </p>
            <div class="hero-accent-bar"></div>
          </div>
          <div class="hero-actions">
            <v-btn
              dark
              outlined
              small
              @click="exportCSV"
              :disabled="personnelList.length === 0"
            >
              <v-icon small left>mdi-file-export</v-icon> ส่งออก CSV
            </v-btn>
          </div>
        </div>
      </div>
    </div>

    <!-- Search Section -->
    <div class="search-section">
      <div class="section-title">
        <i class="mdi mdi-magnify"></i> ค้นหาบุคลากร
      </div>
      <v-row dense align="center">
        <v-col cols="12" md="5">
          <v-text-field
            v-model="searchText"
            placeholder="พิมพ์ชื่อ หรือ นามสกุล เพื่อค้นหา..."
            prepend-inner-icon="mdi-magnify"
            outlined
            dense
            hide-details
            clearable
            @keyup.enter="doSearch"
            @click:clear="clearSearch"
            :loading="loading"
          />
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="filterType"
            :items="typeOptions"
            placeholder="ประเภทบุคลากร"
            prepend-inner-icon="mdi-filter-variant"
            outlined
            dense
            hide-details
            clearable
          />
        </v-col>
        <v-col cols="12" md="2">
          <v-btn
            color="#1B2A4A"
            dark
            block
            depressed
            @click="doSearch"
            :loading="loading"
            :disabled="!canSearch"
            height="40"
          >
            <v-icon left small>mdi-magnify</v-icon> ค้นหา
          </v-btn>
        </v-col>
        <v-col cols="12" md="2">
          <v-btn
            outlined
            color="grey"
            block
            depressed
            @click="clearSearch"
            height="40"
            :disabled="!hasSearched"
          >
            <v-icon left small>mdi-close-circle-outline</v-icon> ล้าง
          </v-btn>
        </v-col>
      </v-row>

      <!-- Search Hint -->
      <div v-if="!hasSearched" class="search-hint mt-3">
        <v-icon small color="#E8913A" class="mr-1"
          >mdi-information-outline</v-icon
        >
        กรุณาพิมพ์ชื่อหรือนามสกุล อย่างน้อย 2 ตัวอักษร แล้วกดปุ่ม "ค้นหา" หรือกด
        Enter
      </div>
    </div>

    <!-- Empty State: ก่อนค้นหา -->
    <div v-if="!hasSearched" class="empty-state">
      <div class="empty-state-inner">
        <v-icon size="80" color="#CBD5E1">mdi-account-search-outline</v-icon>
        <h2 class="empty-title">ค้นหาบุคลากรในสังกัด</h2>
        <p class="empty-desc">
          พิมพ์ชื่อหรือนามสกุลในช่องค้นหาด้านบน<br />
          เพื่อเริ่มค้นหาข้อมูลบุคลากร
        </p>
        <div class="empty-examples">
          <span class="empty-example-label">ตัวอย่าง:</span>
          <v-chip
            v-for="ex in exampleKeywords"
            :key="ex"
            small
            outlined
            color="#1B2A4A"
            class="mx-1 example-chip"
            @click="searchFromExample(ex)"
          >
            {{ ex }}
          </v-chip>
        </div>
      </div>
    </div>

    <!-- No Results State -->
    <div
      v-else-if="hasSearched && !loading && personnelList.length === 0"
      class="empty-state"
    >
      <div class="empty-state-inner">
        <v-icon size="80" color="#CBD5E1">mdi-account-off-outline</v-icon>
        <h2 class="empty-title">ไม่พบผลลัพธ์</h2>
        <p class="empty-desc">
          ไม่พบบุคลากรที่ตรงกับคำค้น "<strong>{{ lastKeyword }}</strong
          >"<br />
          ลองเปลี่ยนคำค้นหาหรือตรวจสอบการสะกด
        </p>
        <v-btn outlined color="#1B2A4A" class="mt-4" @click="clearSearch">
          <v-icon left small>mdi-magnify</v-icon> ค้นหาใหม่
        </v-btn>
      </div>
    </div>

    <!-- Results Section -->
    <template v-else-if="hasSearched && personnelList.length > 0">
      <!-- Result Summary -->
      <div class="result-summary">
        <v-icon small color="#E8913A" class="mr-1">mdi-check-circle</v-icon>
        พบ <strong>{{ totalResults }}</strong> รายการ สำหรับคำค้น "<strong>{{
          lastKeyword
        }}</strong
        >"
        <span v-if="filterType">
          — กรอง: <strong>{{ getTypeName(filterType) }}</strong></span
        >
      </div>

      <!-- Data Table -->
      <div class="table-section">
        <div class="table-header-bar">
          <div class="section-title mb-0">
            <i class="mdi mdi-account-group"></i>
            รายชื่อบุคลากร
            <v-chip small color="primary" dark class="ml-2">
              {{ filteredPersonnel.length }} คน
            </v-chip>
          </div>
        </div>

        <v-data-table
          :headers="tableHeaders"
          :items="filteredPersonnel"
          :items-per-page="10"
          :footer-props="{ 'items-per-page-options': [5, 10, 25, 50] }"
          :loading="loading"
          loading-text="กำลังค้นหา..."
          no-data-text="ไม่พบข้อมูลบุคลากร"
          no-results-text="ไม่พบผลลัพธ์ที่ตรงกัน"
          @click:row="openDetail"
          class="clickable-table"
        >
          <!-- Name Column -->
          <template v-slot:item.fullname="{ item }">
            <div class="name-cell">
              <div
                class="personnel-avatar"
                :style="{
                  background: getAvatarColor(item.personnel_etc_m_type)
                }"
              >
                {{ getInitials(item) }}
              </div>
              <div>
                <div class="name-primary">
                  {{ item.personnel_etc_title
                  }}{{ item.personnel_etc_fristname }}
                  {{ item.personnel_etc_lastname }}
                </div>               
              </div>
            </div>
          </template>

          <!-- Type Column -->
          <template v-slot:item.personnel_etc_m_type="{ item }">
            <v-chip
              small
              class="type-chip"
              :color="getTypeColor(item.personnel_etc_m_type)"
              :dark="!!TYPE_MAP[item.personnel_etc_m_type]"
              :outlined="!TYPE_MAP[item.personnel_etc_m_type]"
            >
              {{ getTypeName(item.personnel_etc_m_type) }}
            </v-chip>
          </template>

          <!-- Level Column (วิทยฐานะ) -->
          <template v-slot:item.personnel_etc_level="{ item }">
            <span>{{ item.personnel_etc_level || "-" }}</span>
          </template>

          <!-- Rang Column (ตำแหน่ง) -->
          <template v-slot:item.personnel_etc_rang="{ item }">
            <span>{{ item.personnel_etc_rang || "-" }}</span>
          </template>

          <!-- College Column -->
          <template v-slot:item.user_firstname="{ item }">
            <span class="college-code">{{
              item.user_firstname || "-"
            }}</span>
          </template>

          <!-- Actions -->
          <template v-slot:item.actions="{ item }">
            <v-btn icon small @click.stop="openDetail(item)">
              <v-icon small color="primary">mdi-eye</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </div>
    </template>

    <!-- Detail Dialog -->
    <v-dialog v-model="detailDialog" max-width="560" scrollable>
      <v-card v-if="selectedPerson" class="detail-card">
        <div class="detail-header">
          <div class="detail-avatar">
            {{ getInitials(selectedPerson) }}
          </div>
          <div class="detail-name">
            {{ selectedPerson.personnel_etc_title
            }}{{ selectedPerson.personnel_etc_fristname }}
            {{ selectedPerson.personnel_etc_lastname }}
          </div>
          <div class="detail-type">
            {{ getTypeName(selectedPerson.personnel_etc_m_type) }}
          </div>
        </div>
        <div class="detail-body">
          <div v-for="(field, fi) in detailFields" :key="fi" class="detail-row">
            <div class="detail-label">{{ field.label }}</div>
            <div class="detail-value">
              {{ getFieldValue(selectedPerson, field.key) || "-" }}
            </div>
          </div>
        </div>
        <v-card-actions class="detail-actions">
          <v-btn text color="grey" @click="detailDialog = false">ปิด</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <personnel-public-group/>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar"
      :color="snackColor"
      top
      right
      :timeout="3000"
    >
      {{ snackMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">ปิด</v-btn>
      </template>
    </v-snackbar>
  </v-app>
</template>

<script>
import PersonnelPublicGroup from '../components/PersonnelPublicGroup.vue';

const TYPE_MAP = {
  director: { name: "ผู้อำนวยการ", color: "#1B2A4A", icon: "mdi-crown" },
  se_director: {
    name: "รองผู้อำนวยการ",
    color: "#2D4373",
    icon: "mdi-account-tie"
  },
  teacher: { name: "ข้าราชการครู", color: "#2980B9", icon: "mdi-school" },
  supervision: {
    name: "ศึกษานิเทศก์",
    color: "#8E44AD",
    icon: "mdi-clipboard-check"
  },
  "38": { name: "บุคลากร 38 ค.(2)", color: "#16A085", icon: "mdi-account-cog" },
  permanent: {
    name: "พนักงานราชการ",
    color: "#27AE60",
    icon: "mdi-badge-account"
  },
  governmentteach: {
    name: "ครูพนักงานราชการ",
    color: "#D35400",
    icon: "mdi-human-male-board"
  },
  governmentsp: {
    name: "สนับสนุน พนง.ราชการ",
    color: "#E67E22",
    icon: "mdi-account-wrench"
  },
  temporary: {
    name: "ครูอัตราจ้าง",
    color: "#E8913A",
    icon: "mdi-account-clock"
  },
  temporary_job: {
    name: "ลูกจ้างชั่วคราว",
    color: "#95A5A6",
    icon: "mdi-account-hard-hat"
  }
};

const BUDGET_MAP = {
  "1": "งบประมาณ",
  "2": "งบรายได้",
  "3": "งบบำรุงการศึกษา",
  "4": "งบอุดหนุน",
  "5": "เงินอุดหนุน (รายได้สถานศึกษา)"
};

export default {
  name: "PersonnelSearch",
  components: {
    PersonnelPublicGroup
  },
  data() {
    return {
      TYPE_MAP,
      loading: false,
      personnelList: [],
      searchText: "",
      filterType: null,
      hasSearched: false,
      lastKeyword: "",
      totalResults: 0,
      detailDialog: false,
      selectedPerson: null,
      snackbar: false,
      snackMessage: "",
      snackColor: "success",

      exampleKeywords: ["Arnie", "Sumie", "เกรซ", "Aquino"],

      tableHeaders: [
        {
          text: "ชื่อ-นามสกุล",
          value: "fullname",
          sortable: false,
          width: "280px"
        },
        { text: "ประเภท", value: "personnel_etc_m_type", width: "150px" },
        { text: "วิทยฐานะ", value: "personnel_etc_level", width: "130px" },
        { text: "ตำแหน่ง", value: "personnel_etc_rang", width: "130px" },
        {
          text: "หน่วยงานสังกัด",
          value: "user_firstname",
          width: "150px"
        },
        {
          text: "วันที่ข้อมูลอัพเดตล่าสุด",
          value: "personnel_etc_lastdate",
          width: "150px"
        },
        
        { text: "", value: "actions", sortable: false, width: "60px" }
      ],

      typeOptions: Object.entries(TYPE_MAP).map(([k, v]) => ({
        text: v.name,
        value: k
      })),

      detailFields: [   
        { label: "ประเภท", key: "_typeName" },
        { label: "วิทยฐานะ", key: "personnel_etc_level" },
        { label: "ตำแหน่ง", key: "personnel_etc_rang" },
        { label: "หน่วยงานสังกัด", key: "user_firstname" },
      ]
    };
  },

  computed: {
    canSearch() {
      return this.searchText && this.searchText.trim().length >= 2;
    },

    filteredPersonnel() {
      let list = this.personnelList;
      // กรองประเภทเพิ่มเติมฝั่ง client (ถ้าเลือก)
      if (this.filterType) {
        list = list.filter(p => p.personnel_etc_m_type === this.filterType);
      }
      return list;
    }
  },

  // ไม่เรียก loadData() ตอน mounted — รอให้ผู้ใช้ค้นหาก่อน
  mounted() {},

  methods: {
    // ==========================================
    // ค้นหาจาก API
    // ==========================================
    async doSearch() {
      if (!this.canSearch) {
        this.showSnack("กรุณาพิมพ์อย่างน้อย 2 ตัวอักษร", "warning");
        return;
      }

      this.loading = true;
      this.lastKeyword = this.searchText.trim();

      try {
        const resp = await this.$http.post("personnel_search.php", {
          ApiKey: "HRvec2021",
          search_keyword: this.lastKeyword,
          personnel_etc_m_type: this.filterType || ""
        });

        if (resp.data.status === "success") {
          this.personnelList = resp.data.data || [];
          this.totalResults = resp.data.total || 0;
          this.hasSearched = true;

          if (this.personnelList.length > 0) {
            this.showSnack(`พบ ${this.totalResults} รายการ`, "success");
          }
        } else {
          this.personnelList = [];
          this.totalResults = 0;
          this.hasSearched = true;
          this.showSnack(resp.data.message || "ไม่พบข้อมูล", "warning");
        }
      } catch {
        this.showSnack("ไม่สามารถเชื่อมต่อ API ได้", "error");
      }

      this.loading = false;
    },

    // ==========================================
    // ล้างการค้นหา — กลับสู่หน้าเริ่มต้น
    // ==========================================
    clearSearch() {
      this.searchText = "";
      this.filterType = null;
      this.personnelList = [];
      this.hasSearched = false;
      this.lastKeyword = "";
      this.totalResults = 0;
    },

    // ==========================================
    // กดตัวอย่างคำค้น
    // ==========================================
    searchFromExample(keyword) {
      this.searchText = keyword;
      this.doSearch();
    },

    // ==========================================
    // Helpers
    // ==========================================
    getTypeName(type) {
      return TYPE_MAP[type] ? TYPE_MAP[type].name : type || "-";
    },
    getTypeColor(type) {
      return TYPE_MAP[type] ? TYPE_MAP[type].color : "#95A5A6";
    },
    getAvatarColor(type) {
      return TYPE_MAP[type] ? TYPE_MAP[type].color : "#95A5A6";
    },
    getInitials(item) {
      const f = (item.personnel_etc_fristname || "").charAt(0);
      const l = (item.personnel_etc_lastname || "").charAt(0);
      return (f + l).toUpperCase();
    },
    getFieldValue(person, key) {
      if (key === "_typeName")
        return this.getTypeName(person.personnel_etc_m_type);
      if (key === "_education") {
        return (
          person.personnel_etc_education_1 ||
          person.personnel_etc_education_2 ||
          person.personnel_etc_education_3 ||
          person.personnel_etc_education_4 ||
          null
        );
      }
      return person[key];
    },
    openDetail(item) {
      this.selectedPerson = item;
      this.detailDialog = true;
    },
    showSnack(msg, color) {
      this.snackMessage = msg;
      this.snackColor = color || "success";
      this.snackbar = true;
    },

    // ==========================================
    // Export CSV
    // ==========================================
    exportCSV() {
      const rows = this.filteredPersonnel;
      if (!rows.length) {
        this.showSnack("ไม่มีข้อมูลสำหรับส่งออก", "warning");
        return;
      }
      const headers = [
        "ชื่อ-นามสกุล",
        "ประเภท",
        "วิทยฐานะ",
        "ตำแหน่ง",
        "หน่วยงานสังกัด"
      ];
      const csvRows = rows.map(r =>
        [
          `${r.personnel_etc_title}${r.personnel_etc_fristname} ${r.personnel_etc_lastname}`,
          this.getTypeName(r.personnel_etc_m_type),
          r.personnel_etc_level || "",
          r.personnel_etc_rang || "",
          r.user_firstname || ""
        ]
          .map(v => `"${v}"`)
          .join(",")
      );
      const BOM = "\uFEFF";
      const csv = BOM + [headers.join(","), ...csvRows].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        "personnel_search_" + new Date().toISOString().slice(0, 10) + ".csv";
      a.click();
      URL.revokeObjectURL(url);
      this.showSnack("ส่งออก CSV สำเร็จ", "success");
    }
  }
};
</script>

<style scoped>
/* Hero Header */
.hero-header {
  background: linear-gradient(135deg, #1b2a4a 0%, #2d4373 60%, #3a5998 100%);
  position: relative;
  overflow: hidden;
}
.hero-header::before {
  content: "";
  position: absolute;
  top: -50%;
  right: -20%;
  width: 600px;
  height: 600px;
  background: radial-gradient(
    circle,
    rgba(232, 145, 58, 0.15) 0%,
    transparent 70%
  );
  border-radius: 50%;
}
.hero-content {
  position: relative;
  z-index: 2;
  padding: 32px 40px 28px;
}
.hero-flex {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}
.hero-title {
  font-size: 28px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 4px;
  letter-spacing: -0.5px;
}
.hero-subtitle {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.65);
  margin: 0;
  font-weight: 300;
}
.hero-accent-bar {
  width: 48px;
  height: 3px;
  background: #e8913a;
  border-radius: 2px;
  margin: 12px 0 0;
}
.hero-actions {
  display: flex;
  gap: 8px;
}

/* Search Section */
.search-section {
  margin: 24px 24px 0;
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 16px rgba(27, 42, 74, 0.05);
  border: 1px solid #e2e8f0;
}
.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #1b2a4a;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.section-title .mdi {
  color: #e8913a;
}
.search-hint {
  font-size: 13px;
  color: #6b7a99;
  display: flex;
  align-items: center;
}

/* Empty State */
.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  padding: 40px 24px;
}
.empty-state-inner {
  text-align: center;
  max-width: 480px;
}
.empty-title {
  font-size: 22px;
  font-weight: 700;
  color: #1b2a4a;
  margin: 20px 0 8px;
}
.empty-desc {
  font-size: 14px;
  color: #6b7a99;
  line-height: 1.7;
  margin: 0;
}
.empty-examples {
  margin-top: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 4px;
}
.empty-example-label {
  font-size: 13px;
  color: #6b7a99;
  margin-right: 4px;
}
.example-chip {
  cursor: pointer !important;
  transition: all 0.2s;
}
.example-chip:hover {
  background: #1b2a4a !important;
  color: #fff !important;
}

/* Result Summary */
.result-summary {
  margin: 20px 24px 0;
  padding: 12px 20px;
  background: #fffbf5;
  border: 1px solid #f5deb3;
  border-radius: 10px;
  font-size: 14px;
  color: #1b2a4a;
  display: flex;
  align-items: center;
}

/* Table */
.table-section {
  margin: 16px 24px 40px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 16px rgba(27, 42, 74, 0.05);
  border: 1px solid #e2e8f0;
  overflow: hidden;
}
.table-header-bar {
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}
.clickable-table {
  cursor: pointer;
}

/* Name Cell */
.name-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}
.personnel-avatar {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  color: #fff;
  flex-shrink: 0;
}
.name-primary {
  font-weight: 600;
  font-size: 14px;
  color: #1b2a4a;
  line-height: 1.3;
}
.name-secondary {
  font-size: 12px;
  color: #6b7a99;
}
.college-code {
  font-size: 13px;
}

/* Detail Dialog */
.detail-card {
  border-radius: 20px !important;
  overflow: hidden;
}
.detail-header {
  background: linear-gradient(135deg, #1b2a4a, #2d4373);
  padding: 32px;
  color: #fff;
  text-align: center;
}
.detail-avatar {
  width: 72px;
  height: 72px;
  border-radius: 20px;
  margin: 0 auto 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 700;
  color: #fff;
  background: rgba(255, 255, 255, 0.15);
  border: 2px solid rgba(255, 255, 255, 0.3);
}
.detail-name {
  font-size: 22px;
  font-weight: 700;
}
.detail-type {
  font-size: 13px;
  opacity: 0.75;
  margin-top: 4px;
}
.detail-body {
  padding: 24px 32px;
}
.detail-row {
  display: flex;
  padding: 12px 0;
  border-bottom: 1px solid #e2e8f0;
}
.detail-row:last-child {
  border-bottom: none;
}
.detail-label {
  width: 140px;
  font-size: 13px;
  color: #6b7a99;
  font-weight: 500;
  flex-shrink: 0;
}
.detail-value {
  font-size: 14px;
  font-weight: 500;
  color: #1b2a4a;
}
.detail-actions {
  padding: 16px 32px 24px;
  justify-content: flex-end;
}
</style>
